/**
 * Owns the flight between rendered frames: the two most recent encounter
 * states (for interpolation), the fixed-timestep accumulator, and the message
 * feed.
 *
 * Deliberately free of three.js and the DOM. The renderer asks it for poses,
 * rounds and a HUD; a headless test drives the identical code path by calling
 * `advance` with a scripted frame time and inputs, which is what makes the
 * scripted-encounter integration tests meaningful rather than tests of a
 * parallel implementation.
 *
 * Everything runs through the engine's encounter layer, including a solo
 * flight — `createFlightRunner` is an encounter of one. That keeps a single
 * tick path rather than two that could drift.
 */

import {
  FIXED_DT,
  FLAT_GROUND,
  NO_SCENERY,
  MAX_CATCHUP_STEPS,
  createEncounter,
  createMissionRun,
  createSimConfig,
  interpolationAlpha,
  isOver,
  planFixedSteps,
  bailOut,
  stepEncounter,
  stepMissionRun,
  type EncounterSetup,
  type EncounterState,
  type FlightInputs,
  type GroundQuery,
  type MissionRun,
  type MissionSpec,
  type Pose,
  type SceneryQuery,
  type SimOptions,
  type SimState,
} from "@red-opus/engine";
import { combatHudFrom, type CombatHud } from "./combat-hud.ts";
import {
  aircraftViewsOf,
  effectSpawnsOf,
  playerPoseOf,
  playerSimOf,
  targetViewsOf,
  type AircraftView,
  type EffectSpawn,
  type TargetView,
} from "./encounter-views.ts";
import { realismSummary } from "./hud-format.ts";
import { hudFrom, type FlightHud } from "./hud-snapshot.ts";
import { encounterMessages, type CalloutContext } from "./sim-messages.ts";
import { isNearingSubject } from "./sim-callouts.ts";
import { CAMEL_FIRST_FLIGHT, PLAYER_ID, roundViews, soloEncounter, type RoundView } from "./encounter-setup.ts";

export interface FlightBriefing {
  /** Display name from the engine's fleet table, e.g. "Sopwith Camel". */
  readonly aircraft: string;
  /**
   * FLIGHT MODEL tier plus every Realism Panel toggle that is on. Named for
   * what it is rather than for where it goes, because since 2026-08-26 it does
   * *not* go in the HUD's standing chip row — it is announced for the opening
   * seconds of the sortie and then hidden (`hud-format.ts`'s
   * `realismChipsVisible`), and it must not be mixed back in with the chips
   * that do stand.
   */
  readonly realismChips: readonly string[];
}

/** What the HUD's corner chip says about this flight, without starting one. */
export function describeFlight(options: SimOptions = CAMEL_FIRST_FLIGHT): FlightBriefing {
  const config = createSimConfig(options);
  return { aircraft: config.spec.name, realismChips: realismSummary(config.tier, config.realism) };
}

/** The gauge readings a flight starts on, before any renderer exists. */
export function initialHud(options: SimOptions = CAMEL_FIRST_FLIGHT): FlightHud {
  return hudFrom(soloState(options));
}

function soloState(options: SimOptions): SimState {
  const encounter = createEncounter(soloEncounter(options));
  const player = encounter.aircraft[0];
  if (!player) throw new Error("a solo encounter must have one aircraft");
  return player.sim;
}

export interface FlightFrame {
  readonly steps: number;
  readonly messages: readonly string[];
  /**
   * Explosion sites the ticks in this frame produced (`effectSpawnsOf`).
   * Collected per *tick* for the same reason messages are: a frame can run
   * several, and only the last one's state survives into `encounter()`, so an
   * event on an intermediate tick would otherwise be lost.
   */
  readonly effects: readonly EffectSpawn[];
  /** True when the frame was so long that simulated time had to be dropped. */
  readonly clamped: boolean;
}

export interface FlightRunner {
  /**
   * Runs whole sim ticks for a rendered frame of `frameSeconds`, and returns
   * what happened. `maxSteps` overrides the default catch-up ceiling
   * (`planFixedSteps`'s own `MAX_CATCHUP_STEPS`) — time compression is the
   * one caller that raises it, so a compressed `frameSeconds` gets a
   * proportionally larger budget instead of clamping at the uncompressed
   * ceiling every frame (`time-compression.ts`).
   */
  advance(frameSeconds: number, inputs: FlightInputs, maxSteps?: number): FlightFrame;
  /** The player's pose to draw: interpolated between the last two sim states. */
  pose(): Pose;
  state(): SimState;
  hud(): FlightHud;
  paused(): boolean;
  setPaused(paused: boolean): void;
  /**
   * The stick that actually produced `state()`, when that can differ from
   * `advance`'s own `inputs` argument. Every live runner's `state()` is a
   * direct consequence of the `inputs` its caller just passed to `advance`,
   * so leaving this unset (the default for `createEncounterRunner` and the
   * duel wire) is correct — the frame loop already holds the right value and
   * has no second source to prefer.
   *
   * `vcr/playback-runner.ts` is the one implementor: a replay ignores
   * `advance`'s `inputs` on purpose (the tape flies itself), so the frame
   * loop's own reading of the live keyboard is the *wrong* stick to paint the
   * cockpit with — this is where it reaches past that for the tape's actual
   * one (`scene-cockpit-report.ts`).
   */
  activeInputs?(): FlightInputs;
}

export interface EncounterRunner extends FlightRunner {
  /**
   * AD-021: the pilot goes over the side. Idempotent and self-guarding — the
   * engine's `bailOut` returns the state unchanged when the jump is not
   * available (on the ground, already gone, already dead), so the caller does
   * not test anything first.
   *
   * Optional on the interface because `vcr/playback-runner.ts` and the duel's
   * `LockstepRunner` do not offer it: a tape flies itself, and `/duel` does not
   * carry the verb at all (the wire has no bit for it — see the design doc).
   */
  bailOut?(): void;
  /** Every aircraft that is not the player, with an interpolated pose. */
  others(): readonly AircraftView[];
  /** Balloons and Zeppelins still worth drawing. */
  targets(): readonly TargetView[];
  /** Every round in flight this frame, as pixel projectiles. */
  rounds(): readonly RoundView[];
  combat(): CombatHud;
  encounter(): EncounterState;
}

/** An encounter runner that is also flying a mission: objectives, outcome, debrief. */
export interface MissionRunner extends EncounterRunner {
  mission(): MissionRun;
  /**
   * Rebuild the run against the region's real ground, once it has one — a
   * no-op after tick 0. The query handed to `createMissionRunner` is still the
   * delegate pointing at `FLAT_GROUND` while the region JSON loads, so a flight
   * that starts on its wheels is seated on the datum; `scene-boot.ts` calls
   * this in the same breath as its ground swap, and like it strictly before the
   * first tick. `mission-spec.ts`'s `seatOnGround` has why it matters.
   */
  reseat(ground: GroundQuery): void;
}

interface RunnerState {
  previous: EncounterState;
  current: EncounterState;
  /**
   * What is under the aeroplanes (`@red-opus/engine`'s `GroundQuery`). Flat
   * datum here, which is what this port's world actually is — the elevation
   * lane implements the same interface off `.GI` hill data and it gets passed
   * in from `createFlightScene` without anything else changing.
   */
  ground: GroundQuery;
  /**
   * What is *standing* on it (`@red-opus/engine`'s `SceneryQuery`, AD-018) — the
   * region's buildings, as boxes a strafing round can go into. `NO_SCENERY`
   * until `fly-region.ts` has a region, and under that default the engine's
   * scenery pass is a no-op.
   */
  scenery: SceneryQuery;
  /**
   * Set when this runner is flying a `MissionSpec`. The tick then goes through
   * `stepMissionRun` so objectives advance with the encounter; without it the
   * runner is a bare encounter, which is still what the instant dogfight wants.
   */
  mission: MissionRun | null;
  accumulator: number;
  paused: boolean;
  /** Last message shown, so a per-tick event (ground contact especially) does not spam the line. */
  lastMessage: string;
  /**
   * The two mission-level callouts (G33) are edge-triggered, and the edge is
   * between two `MissionRun`s rather than two `EncounterState`s — so the runner
   * carries the previous tick's answers. Both are levels the caller diffs, not
   * "already said" flags: replaying the same tape re-derives them identically,
   * which is the determinism rule `sim-callouts.ts` is written to.
   */
  wasNearing: boolean;
  missionAnnounced: boolean;
}

function playerSim(state: EncounterState): SimState {
  return playerSimOf(state, PLAYER_ID);
}

/** One tick, through the mission layer when there is one and the encounter layer when there is not. */
function advanceOne(runner: RunnerState, inputs: FlightInputs): EncounterState {
  const keyed = { [PLAYER_ID]: inputs };
  if (!runner.mission) return stepEncounter(runner.current, keyed, FIXED_DT, runner.ground, runner.scenery);
  runner.mission = stepMissionRun(runner.mission, keyed, FIXED_DT, runner.ground, runner.scenery);
  return runner.mission.encounter;
}

/** What a frame's worth of ticks produced beyond the new state itself. */
interface TickHarvest {
  readonly messages: string[];
  readonly effects: EffectSpawn[];
}

function runTicks(runner: RunnerState, steps: number, inputs: FlightInputs): TickHarvest {
  const messages: string[] = [];
  const effects: EffectSpawn[] = [];
  for (let index = 0; index < steps; index++) {
    const previous = runner.current;
    const next = advanceOne(runner, inputs);
    runner.previous = previous;
    runner.current = next;
    effects.push(...effectSpawnsOf(next));
    for (const message of encounterMessages(previous, next, PLAYER_ID, missionCallouts(runner))) {
      if (message === runner.lastMessage) continue;
      runner.lastMessage = message;
      messages.push(message);
    }
  }
  return { messages, effects };
}

/**
 * The mission's own contribution to the tick's callouts: the approach cue on the
 * tick the player first comes inside range of what the mission is about, and
 * `THE MISSION IS COMPLETE.` on the tick it is. Both edges are taken here
 * because this is where the previous `MissionRun` lives.
 */
function missionCallouts(runner: RunnerState): CalloutContext {
  const run = runner.mission;
  if (!run) return {};
  const nearing = isNearingSubject(run);
  const crossed = nearing && !runner.wasNearing;
  runner.wasNearing = nearing;
  const completed = run.outcome === "success" && !runner.missionAnnounced;
  if (completed) runner.missionAnnounced = true;
  return { nearing: crossed ? run.spec.type : undefined, missionCompleted: completed };
}

/** True once a mission this runner is flying has been won or lost. */
function missionDecided(runner: RunnerState): boolean {
  return runner.mission !== null && runner.mission.outcome !== "inProgress";
}

/**
 * True once the encounter itself is over — win, loss or mutual destruction —
 * regardless of whether a mission is riding along. A mission's own outcome
 * usually lands on the same tick (`resolveOutcome`'s "a pilot who has been
 * shot down has not completed the mission" rule), so this mostly guards the
 * *bare* encounter the instant dogfight flies, which has no mission to freeze
 * it: without this, the sim kept ticking under the outcome banner forever,
 * the aeroplane still flyable while its opponent lay in a smoking heap.
 */
function encounterDecided(runner: RunnerState): boolean {
  return isOver(runner.current.status);
}

function newRunnerState(
  initial: EncounterState,
  mission: MissionRun | null,
  ground: GroundQuery,
  scenery: SceneryQuery,
): RunnerState {
  return {
    previous: initial,
    current: initial,
    mission,
    ground,
    scenery,
    accumulator: 0,
    paused: false,
    lastMessage: "",
    wasNearing: mission ? isNearingSubject(mission) : false,
    missionAnnounced: false,
  };
}

function createRunner(runner: RunnerState): EncounterRunner {
  const alpha = () => interpolationAlpha(runner.accumulator);

  return {
    advance(frameSeconds, inputs, maxSteps = MAX_CATCHUP_STEPS) {
      // A decided mission (or a decided bare encounter) stops the clock as
      // well as the sim. `stepMissionRun` already freezes the state, but
      // letting the accumulator keep running would leave the *interpolated*
      // pose sliding between the last two ticks for ever — a visible
      // sub-metre wobble under the debriefing screen.
      if (runner.paused || missionDecided(runner) || encounterDecided(runner)) {
        return { steps: 0, messages: [], effects: [], clamped: false };
      }
      const plan = planFixedSteps(runner.accumulator, frameSeconds, FIXED_DT, maxSteps);
      runner.accumulator = plan.accumulator;
      const harvest = runTicks(runner, plan.steps, inputs);
      return { steps: plan.steps, messages: harvest.messages, effects: harvest.effects, clamped: plan.clamped };
    },
    pose: () => playerPoseOf(runner.previous, runner.current, PLAYER_ID, alpha()),
    state: () => playerSim(runner.current),
    hud: () => hudFrom(playerSim(runner.current)),
    others: () => aircraftViewsOf(runner.previous, runner.current, PLAYER_ID, alpha()),
    targets: () => targetViewsOf(runner.current),
    rounds: () => roundViews(runner.current),
    combat: () => combatHudFrom(runner.current, PLAYER_ID),
    encounter: () => runner.current,
    // AD-021. Applied to `current` *and* to the mission's copy of it when there
    // is one, because `stepMissionRun` steps `runner.mission.encounter` and not
    // `runner.current` — writing only one of the two would have the pilot jump
    // out of an encounter the mission layer has never heard of, and the canopy
    // would vanish on the next tick.
    bailOut: () => {
      const jumped = bailOut(runner.current, PLAYER_ID);
      if (jumped === runner.current) return;
      runner.current = jumped;
      if (runner.mission) runner.mission = { ...runner.mission, encounter: jumped };
    },
    paused: () => runner.paused,
    setPaused: (paused) => {
      runner.paused = paused;
    },
  };
}

export function createEncounterRunner(
  setup: EncounterSetup,
  ground: GroundQuery = FLAT_GROUND,
  scenery: SceneryQuery = NO_SCENERY,
): EncounterRunner {
  return createRunner(newRunnerState(createEncounter(setup), null, ground, scenery));
}

/**
 * A runner flying a `MissionSpec`: the same frame loop, with objectives and a
 * debrief riding along. `mission()` is what the HUD and the debrief screen read.
 */
export function createMissionRunner(
  spec: MissionSpec,
  ground: GroundQuery = FLAT_GROUND,
  scenery: SceneryQuery = NO_SCENERY,
): MissionRunner {
  // The same `ground` the run is stepped with, so a flight that starts on its
  // wheels is seated on the terrain under it rather than on the flat datum its
  // generator had to assume (`mission-spec.ts`'s `seatOnGround`).
  const run = createMissionRun(spec, ground);
  const state = newRunnerState(run.encounter, run, ground, scenery);
  return {
    ...createRunner(state),
    mission: () => state.mission ?? run,
    reseat(next) {
      // `current.tick` is the encounter's own clock: a flight already under way
      // (or resumed) keeps the ground it started on.
      if (state.current.tick !== 0 || !state.mission) return;
      const rebuilt = createMissionRun(spec, next);
      state.mission = rebuilt;
      state.previous = rebuilt.encounter;
      state.current = rebuilt.encounter;
      state.wasNearing = isNearingSubject(rebuilt);
    },
  };
}

/** A flight with nobody else in the sky — the same tick path, one aircraft on the roster. */
export function createFlightRunner(
  options: SimOptions = CAMEL_FIRST_FLIGHT,
  ground: GroundQuery = FLAT_GROUND,
  scenery: SceneryQuery = NO_SCENERY,
): FlightRunner {
  return createEncounterRunner(soloEncounter(options), ground, scenery);
}
